import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import { PRODUCT_TITLE } from "@/constants/envs";
import AuthLayout from "@/layouts/AuthLayout.vue";
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import ErrorLayout from "@/layouts/ErrorLayout.vue";
import { useAuthStore } from "@/stores/auth";
import { pendingRequests } from "@/utils/apis";
import { goLoginPage } from "@/utils/commands";
import { toastError } from "@/utils/toaster";

declare module "vue-router" {
  interface RouteMeta {
    /** 사이드바 active 표시·아코디언 자동 열기용. `src/definitions/navigation.ts` 의 pageId 와 일치해야 한다. */
    pageId?: string;
    /** 상단바 breadcrumb · `.page-title` 과 탭 제목(`document.title`, 「{title} · 셀렉허브」)에 표시한다. */
    title?: string;
    /** 계정 없이 들어오는 화면. `App.vue` 가 마운트 시 로그인 리다이렉트에서 제외한다. */
    public?: boolean;
    /**
     * 화면 히어로 제목(h1)이 `title` 과 같은 글이다(89차 C89-06 · RG89-05). 390(≤620)의 상단바 위치 줄은 마지막 칸(= 화면 이름) 대신
     * 가장 가까운 그룹 칸을 보인다 — 바로 아래 h1 과 같은 글을 되풀이하지 않고 새 정보(어느 단계 묶음인가)를 준다. 기본 false.
     * h1 이 표어(직무분석 · 역량 · 운영 화면 등)면 달지 않는다 — 그쪽은 위치 줄이 메뉴 이름을 알리는 유일한 자리다.
     * 그룹 경로가 없는 시작 루트 화면(`start/workspace`)은 ≤620 에서 화면 이름 대신 루트 「셀렉허브」 만 보인다(90차 RG90-02).
     */
    heroIsTitle?: boolean;
    /**
     * 사이드바에 없는 하위 화면의 부모 메뉴 이름(「전체 현황 관리」). 위치 줄이 「셀렉허브 / 부모 / 이 화면」 으로 읽히고,
     * ≤620 에서는 부모 칸이 눈에 보이는 한 줄이다(89차 C89-06 · UXC89-10).
     */
    parentTitle?: string;
  }
}

/**
 * 라우팅
 * - 각 라우트의 `meta.pageId` 는 `src/definitions/navigation.ts` 의 pageId 와 일치해야 한다.
 *   (사이드바 active 표시 및 아코디언 자동 열기에 사용)
 * - `meta.title` 은 상단바 breadcrumb / page-title 과 탭 제목(`document.title`)에 표시된다.
 * - 페이지 컴포넌트는 모두 lazy import 한다.
 */

const requireAuth = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
) => {
  const { loggedIn } = useAuthStore();
  if (!loggedIn) {
    // 열려던 화면을 실어 보내 로그인 뒤 돌아오게 한다(25차 M25-01).
    await goLoginPage(to.fullPath);
    return false;
  }
};

const start: RouteRecordRaw[] = [
  {
    path: "start/workspace",
    component: () => import("@/views/start/WorkspacePage.vue"),
    meta: {
      pageId: "workspace",
      title: "워크스페이스 생성",
      heroIsTitle: true,
    },
  },
  {
    path: "start/status",
    component: () => import("@/views/start/StatusMatrixPage.vue"),
    meta: { pageId: "statusmatrix", title: "전체 현황 관리" },
  },
  {
    // 설계서 "리스크 알림 클릭 → 리스크 상세 화면". 사이드바에는 없어 전체 현황 관리를 active 로 둔다.
    path: "start/status/risks/:riskKey",
    component: () => import("@/views/start/RiskDetailPage.vue"),
    meta: {
      pageId: "statusmatrix",
      title: "리스크 상세",
      parentTitle: "전체 현황 관리",
    },
  },
  // 개편(2026-10-13 ¶5): 회사 대시보드를 전체 현황 관리로 합쳤다. 옛 북마크 · 메일 링크는 새 경로로 보낸다.
  { path: "start/dashboard", redirect: "/start/status" },
  {
    path: "start/dashboard/risks/:riskKey",
    redirect: (to) => ({
      path: `/start/status/risks/${String(to.params.riskKey ?? "")}`,
      query: to.query,
    }),
  },
];

const quickHire: RouteRecordRaw[] = [
  {
    path: "quick-hire",
    component: () => import("@/views/quick-hire/QuickHirePage.vue"),
    meta: { pageId: "applicantai", title: "빠르게 채용하기" },
  },
];

const orgFit: RouteRecordRaw[] = [
  {
    path: "org-fit/mission",
    component: () => import("@/views/org-fit/MissionPage.vue"),
    meta: { pageId: "mission", title: "미션 설계", heroIsTitle: true },
  },
  {
    path: "org-fit/vision",
    component: () => import("@/views/org-fit/VisionPage.vue"),
    meta: {
      pageId: "vision",
      title: "비전·전략목표·수행과제",
      heroIsTitle: true,
    },
  },
  {
    path: "org-fit/core-values",
    component: () => import("@/views/org-fit/CoreValuesPage.vue"),
    meta: { pageId: "core", title: "핵심가치 설계", heroIsTitle: true },
  },
  {
    path: "org-fit/talent",
    component: () => import("@/views/org-fit/TalentPage.vue"),
    meta: { pageId: "talent", title: "인재상 도출", heroIsTitle: true },
  },
  {
    path: "org-fit/value-system",
    component: () => import("@/views/org-fit/ValueSystemPage.vue"),
    meta: { pageId: "valuesystem", title: "가치관 체계도", heroIsTitle: true },
  },
];

const jobFit: RouteRecordRaw[] = [
  {
    path: "job-fit/job-analysis",
    component: () => import("@/views/job-fit/JobAnalysisPage.vue"),
    meta: { pageId: "jobanalysis", title: "직무분석" },
  },
  {
    path: "job-fit/dictionary",
    component: () => import("@/views/job-fit/JobDictionaryPage.vue"),
    meta: { pageId: "jobdictionary", title: "직무 사전", heroIsTitle: true },
  },
  {
    path: "job-fit/competency",
    component: () => import("@/views/job-fit/CompetencyPage.vue"),
    meta: { pageId: "competency", title: "역량모델링" },
  },
  {
    path: "job-fit/ai-literacy",
    component: () => import("@/views/job-fit/AiLiteracyPage.vue"),
    meta: { pageId: "ailiteracy", title: "AI 리터러시" },
  },
  {
    path: "job-fit/application-design",
    component: () => import("@/views/job-fit/ApplicationDesignPage.vue"),
    meta: { pageId: "applicationdesign", title: "입사지원서 자동 설계하기" },
  },
];

const operations: RouteRecordRaw[] = [
  {
    // 개편(¶53) 운영하기 첫 메뉴. 실제 기능은 운영 수정본 뒤라 지금은 빈 화면(쓰기 없음)이다.
    path: "operations/careers-page",
    component: () => import("@/views/operations/CareersPage.vue"),
    meta: {
      pageId: "homepagebuilder",
      title: "채용 홈페이지 만들기",
      heroIsTitle: true,
    },
  },
  {
    path: "operations/applicants",
    component: () => import("@/views/operations/ApplicantsPage.vue"),
    meta: { pageId: "applicants", title: "지원자 관리" },
  },
  {
    path: "operations/interview-prep",
    component: () => import("@/views/operations/InterviewPrepPage.vue"),
    meta: { pageId: "interviewops", title: "면접준비" },
  },
  {
    path: "operations/interview-console",
    component: () => import("@/views/operations/InterviewConsolePage.vue"),
    meta: { pageId: "interview", title: "실시간 면접 콘솔" },
  },
  {
    path: "operations/reference-check",
    component: () => import("@/views/operations/ReferenceCheckPage.vue"),
    meta: { pageId: "referencecheck", title: "레퍼런스 체크" },
  },
  {
    path: "operations/decision",
    component: () => import("@/views/operations/DecisionPage.vue"),
    meta: { pageId: "decision", title: "최종 의사결정" },
  },
  {
    path: "operations/reports",
    component: () => import("@/views/operations/ReportsPage.vue"),
    meta: { pageId: "reports", title: "결과보고서" },
  },
  {
    path: "operations/onboarding-docs",
    component: () => import("@/views/operations/OnboardingDocsPage.vue"),
    meta: { pageId: "onboardingdocs", title: "입사서류 요청하기" },
  },
];

const management: RouteRecordRaw[] = [
  {
    path: "management/analytics",
    component: () => import("@/views/management/AnalyticsPage.vue"),
    meta: { pageId: "analytics", title: "셀렉허브 애널리틱스" },
  },
  {
    path: "management/ai-governance",
    component: () => import("@/views/management/AiGovernancePage.vue"),
    meta: { pageId: "aigovernance", title: "AI 거버넌스" },
  },
  {
    path: "management/admin",
    component: () => import("@/views/management/SaasAdminPage.vue"),
    meta: { pageId: "admin", title: "SaaS 관리자" },
  },
  {
    path: "management/guardrails",
    component: () => import("@/views/management/GuardrailsPage.vue"),
    meta: { pageId: "guardrails", title: "공정채용 체크" },
  },
];

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    component: DefaultLayout,
    beforeEnter: requireAuth,
    children: [
      { path: "", redirect: "/start/status" },
      ...start,
      ...quickHire,
      ...orgFit,
      ...jobFit,
      ...operations,
      ...management,
    ],
  },
  {
    path: "/login",
    component: AuthLayout,
    // 계정 없이 들어오는 화면. App.vue 의 마운트 시 로그인 리다이렉트에서 제외한다.
    meta: { public: true },
    children: [
      {
        path: "",
        component: () => import("@/views/login/LoginPage.vue"),
        meta: { title: "로그인" },
      },
    ],
  },
  {
    // 회원가입은 아직 계정이 없는 사용자가 들어오므로 인증 가드를 걸지 않는다.
    // 인증 메일 링크(/signup/verify)는 새 탭에서 열려 앱이 새로 마운트되므로 public 표시가 꼭 필요하다.
    path: "/signup",
    component: AuthLayout,
    meta: { public: true },
    children: [
      {
        path: "",
        component: () => import("@/views/signup/SignupPage.vue"),
        meta: { title: "회원가입" },
      },
      {
        // 인증 메일 링크의 도착지. 토큰을 확인하고 회원가입 화면으로 돌려보낸다.
        path: "verify",
        component: () => import("@/views/signup/SignupVerifyPage.vue"),
        meta: { title: "이메일 인증" },
      },
    ],
  },
  {
    // 담당자 입력 링크의 도착지. 현직자·팀장·HR·임원이 계정 없이 메일 링크로 들어오므로
    // 인증 가드를 걸지 않는다. 화면이 자체 셸을 그리므로 레이아웃 컴포넌트를 두지 않는다.
    path: "/job-input/:token",
    component: () => import("@/views/job-fit/JobInputPage.vue"),
    meta: { public: true, title: "직무 입력 요청" },
  },
  {
    // 입사서류 제출 링크의 도착지. 최종합격자가 계정 없이 메일 · 문자 링크로 들어오므로 인증 가드를 걸지 않는다.
    // 담당자 입력 링크와 같이 화면이 자체 셸을 그리고, 경로의 토큰은 `utils/sentry.ts` 가 가린다.
    path: "/onboarding-upload/:token",
    component: () => import("@/views/operations/OnboardingUploadPage.vue"),
    meta: { public: true, title: "입사서류 제출" },
  },
  {
    // 채용 페이지 지원 링크의 도착지. 지원자가 계정 없이 열기 때문에 인증 가드를 걸지 않는다.
    // 화면이 자체 셸을 그리고, 경로의 slug 는 서버로 본문에 실어 보낸다(조회도 POST).
    path: "/apply/:slug",
    component: () => import("@/views/operations/PublicApplyPage.vue"),
    meta: { public: true, title: "채용 지원" },
  },
  {
    path: "/error",
    component: ErrorLayout,
    children: [
      {
        path: "",
        component: () => import("@/views/error/UnexpectedPage.vue"),
        meta: { title: "오류" },
      },
      {
        path: "404",
        component: () => import("@/views/error/NotFoundPage.vue"),
        meta: { title: "페이지를 찾을 수 없음" },
      },
    ],
  },
  {
    // 매칭되지 않는 경로는 404로 보낸다.
    path: "/:catchAll(.*)",
    redirect: "/error/404",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from) => {
  // 화면이 바뀌면 진행 중인 API 요청을 모두 취소한다.
  //
  // 같은 경로에서 쿼리·해시만 바뀌는 이동은 건너뛴다. 페이지는 `RouterView` 의 key(워크스페이스 id + 경로)가
  // 같아 다시 마운트되지 않으므로 취소할 이유가 없고, 취소하면 해를 끼친다 — 첫 진입 때 페이지가 쿼리를
  // `router.replace` 로 맞추는 순간 `App.vue` 의 `GET /auth/me` 까지 끊겨 역할이 반영되지 않았고(6차 E8),
  // 페이지 자신의 조회도 끊긴 채 다시 불리지 않았다. 워크스페이스 전환은 `DefaultLayout` 이 따로 취소한다.
  if (to.path === from.path) {
    return;
  }
  pendingRequests.forEach((controller) => {
    controller.abort(`Route change: ${from.path} to ${to.path}`);
  });
  pendingRequests.clear();
});

/**
 * 청크(동적 import) 로드 실패 처리(30차 P30-03). 배포가 `--delete` 동기화로 옛 청크를 지웠거나(배포의 1%) 옛 `index.html` 이 새 배포 뒤에도 쓰이면, 화면 이동이
 * 조용히 아무 일도 없거나(메뉴) 레이아웃까지 흰 화면(딥링크)이 된다. 한 번만 이동하려던 주소로 전체 새로고침해 새 `index.html` 을 받고, 표식이 이미 있으면(새로고침해도
 * 같은 실패) 무한 새로고침 대신 안내만 띄운다. 표식은 이동이 한 번 성공하면 지운다.
 */
const CHUNK_RELOAD_KEY = "selechub-chunk-reload";
/** 같은 표식이 이 시간(ms) 안에 있으면 방금 새로고침했는데도 실패한 것으로 본다 */
const CHUNK_RELOAD_WINDOW_MS = 60_000;

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk .* failed/i.test(
    message,
  );
}

router.onError((error, to) => {
  if (!isChunkLoadError(error)) return;
  let reloadedRecently = false;
  try {
    const marked = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY));
    reloadedRecently =
      marked > 0 && Date.now() - marked < CHUNK_RELOAD_WINDOW_MS;
    if (!reloadedRecently) {
      window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    }
  } catch {
    // 저장소를 못 쓰면(차단 · 사생활 보호) 표식을 못 남겨 무한 새로고침 위험이 있으므로 새로고침하지 않고 안내만 한다.
    reloadedRecently = true;
  }
  if (!reloadedRecently) {
    window.location.assign(to.fullPath);
    return;
  }
  toastError(
    "새 버전이 배포되어 화면을 불러오지 못했습니다. 브라우저를 새로고침(Ctrl/Cmd + Shift + R)한 뒤 다시 시도해 주세요.",
  );
});

/**
 * 탭 제목(77차 UXS77-03) — 「{화면} · 셀렉허브」. 모든 화면이 같은 이름이면 탭을 여럿 열 때 · 뒤로 가기 목록 · 방문 기록에서 구분이 안 되고
 * 화면 읽기는 페이지 제목(WCAG 2.4.2)을 읽을 수 없다. `meta.title` 은 상단바와 같은 값이다. 제목이 없는 라우트는 제품 이름만.
 * 레이아웃마다 두지 않고 라우터 한 곳에서 맡는다(로그인 · 오류 · 공개 링크 화면도 같은 규칙).
 */
function documentTitleFor(to: RouteLocationNormalized): string {
  const title = to.meta.title;
  return title ? `${title} · ${PRODUCT_TITLE}` : PRODUCT_TITLE;
}

router.afterEach((to, _from, failure) => {
  // 이동이 성공했으면 표식을 지운다(다음 배포 때 다시 한 번 자동 새로고침할 수 있게).
  if (failure) return;
  document.title = documentTitleFor(to);
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // 저장소를 못 쓰는 환경은 위에서 이미 안내만 하도록 처리했다.
  }
});

export default router;
